import { Range } from 'react-range'
import { useState } from 'react'
import { RangeThumbTooltip } from './RangeTooltip'

interface InputRangeProps {
  id: string
  label: string
  values: number[]
  onChange: (values: number[]) => void
  min: number
  max: number
  step?: number
  customColor?: string
  formatMinMaxLabels: (value: number) => string
  formatTooltipDisplay: (value: number) => React.ReactNode
  disabled?: boolean
}

export const InputRange = ({
  id,
  label,
  values,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  customColor = '#c7d2fe',
  formatMinMaxLabels,
  formatTooltipDisplay,
}: InputRangeProps) => {
  const [activeThumb, setActiveThumb] = useState<number | null>(null)

  const left = `${((values[0] - min) / (max - min)) * 100}%`
  const width = `${((values[1] - values[0]) / (max - min)) * 100}%`

  return (
    <div id={id} className='relative w-full'>
      <div className='absolute left-0 top-[-28px] w-40 text-black text-sm font-normal leading-none tracking-wide'>
        {label}
      </div>

      <Range
        step={step}
        min={min}
        max={max}
        values={values}
        disabled={disabled}
        onChange={(newValues) => onChange(newValues as number[])}
        renderTrack={({ props, children }) => (
          <div
            {...props}
            className='w-full h-[10px] relative'
            style={props.style}
          >
            <div className='absolute top-2 left-0 right-0 bottom-2 bg-white border border-[#bbd2d7] rounded-[5px]' />
            <div
              className='absolute top-0 h-full rounded-[5px]'
              style={{
                left,
                width,
                backgroundColor: customColor,
              }}
            />
            {children}
          </div>
        )}
        renderThumb={({ props, index }) => {
          const value = values[index]

          return (
            <div
              {...props}
              onMouseEnter={() => setActiveThumb(index)}
              onMouseLeave={() => setActiveThumb(null)}
              onMouseDown={() => setActiveThumb(index)}
              onMouseUp={() => setActiveThumb(null)}
              className='relative'
              style={props.style}
            >
              {activeThumb === index && (
                <RangeThumbTooltip
                  formattedDisplay={formatTooltipDisplay(value)}
                />
              )}
              <div className='relative shadow-[0.1px_0.1px_0.8px_rgba(32,84,151,1),-0.1px_-0.1px_0.8px_rgba(32,84,151,1)] flex items-center justify-center w-5 h-5 bg-white rounded-full'>
                <div
                  className='absolute w-3 h-3 rounded-full'
                  style={{ backgroundColor: customColor }}
                />
              </div>
            </div>
          )
        }}
      />

      <div className='relative w-full mt-2 text-gray-900 text-sm font-normal leading-tight tracking-wide'>
        <div className='absolute left-0'>{formatMinMaxLabels(min)}</div>

        <div className='absolute right-0 text-right'>
          {formatMinMaxLabels(max)}
        </div>
      </div>
    </div>
  )
}
